// frontend/src/components/Samples/MyTasksCalendar.jsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { lineOperationsAPI, customersAPI, usersAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { useAuth } from '../../context/AuthContext';
import SearchableSelect from '../Common/SearchableSelect';
import CalendarView from '../Common/CalendarView';
import { FiSearch, FiX } from 'react-icons/fi';

const STATUS_COLOR = {
  NEW:         '#6b7280',
  IN_REVIEW:   '#8b5cf6',
  IN_PROGRESS: '#2563eb',
  ON_HOLD:     '#d97706',
  COMPLETED:   '#059669',
  CANCELLED:   '#dc2626',
};

const STATUS_LABEL = {
  NEW:         'Novo',
  IN_REVIEW:   'Em Análise',
  IN_PROGRESS: 'Em Curso',
  ON_HOLD:     'Em Espera',
  COMPLETED:   'Concluído',
  CANCELLED:   'Cancelado',
};

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'Todos os estados' },
  ...Object.entries(STATUS_LABEL).map(([k, v]) => ({ value: k, label: v })),
];

const MyTasksCalendar = () => {
  const navigate           = useNavigate();
  const { t }              = useTranslation();
  const { user: authUser } = useAuth();

  const [users,      setUsers]      = useState([]);
  const [areas,      setAreas]      = useState([]);
  const [operations, setOperations] = useState([]);
  const [customers,  setCustomers]  = useState([]);
  const [tasks,      setTasks]      = useState([]);
  const [loading,    setLoading]    = useState(true);

  const getDefaultFilters = useCallback(() => ({
    status:          'ALL',
    user_id:         authUser?.user_id ? String(authUser.user_id) : '',
    area_id:         '',
    operation_id:    '',
    sample_order_no: '',
    customer_id:     '',
    erp_product:     '',
  }), [authUser?.user_id]);

  const [filters, setFilters] = useState(() => ({
    status:          'ALL',
    user_id:         authUser?.user_id ? String(authUser.user_id) : '',
    area_id:         '',
    operation_id:    '',
    sample_order_no: '',
    customer_id:     '',
    erp_product:     '',
  }));
  const [textDraft, setTextDraft] = useState({ sample_order_no: '', erp_product: '' });

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [areasRes, opsRes, customersRes, usersRes] = await Promise.all([
          lineOperationsAPI.getAreas(),
          lineOperationsAPI.getOperationTypes(),
          customersAPI.getAll(),
          usersAPI.getAll(),
        ]);
        if (areasRes?.data?.success)     setAreas(areasRes.data.data || []);
        if (opsRes?.data?.success)       setOperations(opsRes.data.data || []);
        if (customersRes?.data?.success) setCustomers(customersRes.data.data || []);
        if (usersRes?.data?.success)     setUsers(usersRes.data.users || usersRes.data.data || []);
      } catch { /* non-critical */ }
    };
    loadLookups();
  }, []);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ limit: 500 });
      if (filters.status)          q.set('status',          filters.status);
      if (filters.user_id)         q.set('user_id',         filters.user_id);
      if (filters.area_id)         q.set('area_id',         filters.area_id);
      if (filters.operation_id)    q.set('operation_id',    filters.operation_id);
      if (filters.sample_order_no) q.set('sample_order_no', filters.sample_order_no);
      if (filters.customer_id)     q.set('customer_id',     filters.customer_id);
      if (filters.erp_product)     q.set('erp_product',     filters.erp_product);

      const res = await lineOperationsAPI.getMyTasks(q.toString());
      if (res.data.success) {
        // Deduplicate by public_id — LEFT JOIN on sample_order_lines can
        // produce duplicate rows when matrix_line_no has multiple line entries
        const seen = new Set();
        const deduped = (res.data.data || []).filter(row => {
          if (seen.has(row.public_id)) return false;
          seen.add(row.public_id);
          return true;
        });
        setTasks(deduped);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value ?? '' }));
  };

  const handleTextCommit = (field) => {
    setFilters(prev => ({ ...prev, [field]: textDraft[field] }));
  };

  const hasActiveFilters = (() => {
    const def = getDefaultFilters();
    return Object.keys(def).some(k => filters[k] !== def[k]);
  })();

  const clearFilters = () => {
    setFilters(getDefaultFilters());
    setTextDraft({ sample_order_no: '', erp_product: '' });
  };

  const events = useMemo(() => tasks
    .filter(task => task.planned_end_date)
    .map(task => ({
      id:      task.public_id,
      title:   task.operation_label,
      date:    task.planned_end_date.slice(0, 10),
      color:   STATUS_COLOR[task.status] || '#6b7280',
      badge:   task.sample_order_no,
      tooltip: [
        task.operation_label,
        task.sample_order_no,
        task.area_code,
        task.customer_name,
        STATUS_LABEL[task.status],
      ].filter(Boolean).join(' · '),
      onClick: () => navigate(`/samples/orders/${task.order_public_id}`),
    })),
  [tasks, navigate]);

  return (
    <div className="page-container">

      <div className="order-filters" style={{ marginBottom: 16 }}>

        {/* Row 1 — SearchableSelects */}
        <div className="filters-row">

          <div className="filter-group">
            <label>Utilizador</label>
            <SearchableSelect
              value={filters.user_id}
              onChange={v => handleFilterChange('user_id', v)}
              options={users.map(u => ({ value: String(u.user_id), label: u.full_name || u.username }))}
              getOptionLabel={o => o.label}
              getOptionValue={o => o.value}
              formatDisplay={o => o.label}
              placeholder="Todos os utilizadores"
              isClearable
            />
          </div>

          <div className="filter-group">
            <label>Área</label>
            <SearchableSelect
              value={filters.area_id}
              onChange={v => handleFilterChange('area_id', v)}
              options={areas.map(a => ({
                value: String(a.area_id),
                label: a.description ? `${a.area_code} – ${a.description}` : a.area_code,
              }))}
              getOptionLabel={o => o.label}
              getOptionValue={o => o.value}
              formatDisplay={o => o.label}
              placeholder="Todas as áreas"
              isClearable
            />
          </div>

          <div className="filter-group">
            <label>Tarefa</label>
            <SearchableSelect
              value={filters.operation_id}
              onChange={v => handleFilterChange('operation_id', v)}
              options={operations.map(op => ({
                value: String(op.operation_id),
                label: op.operation_label,
              }))}
              getOptionLabel={o => o.label}
              getOptionValue={o => o.value}
              formatDisplay={o => o.label}
              placeholder="Todas as tarefas"
              isClearable
            />
          </div>

          <div className="filter-group">
            <label>Cliente</label>
            <SearchableSelect
              value={filters.customer_id}
              onChange={v => handleFilterChange('customer_id', v)}
              options={customers.map(c => ({
                value: String(c.customer_id),
                label: c.customer_name || c.name,
              }))}
              getOptionLabel={o => o.label}
              getOptionValue={o => o.value}
              formatDisplay={o => o.label}
              placeholder="Todos os clientes"
              isClearable
            />
          </div>

          <div className="filter-group filter-group-sm">
            <label>Estado</label>
            <SearchableSelect
              value={filters.status}
              onChange={v => handleFilterChange('status', v ?? 'ALL')}
              options={STATUS_OPTIONS}
              getOptionLabel={o => o.label}
              getOptionValue={o => o.value}
              formatDisplay={o => o.label}
              placeholder="Todos os estados"
              isClearable
            />
          </div>

          <div className="filter-group filter-group-sm">
            <label>Nº Pedido</label>
              <FiSearch size={13} className="filter-search-icon" />
              <input
                type="text"
                className="form-control"
                value={textDraft.sample_order_no}
                onChange={e => setTextDraft(d => ({ ...d, sample_order_no: e.target.value }))}
                onBlur={() => handleTextCommit('sample_order_no')}
                onKeyDown={e => e.key === 'Enter' && handleTextCommit('sample_order_no')}
              />
          </div>

          <div className="filter-group filter-group-sm">
            <label>Produto ERP</label>
              <FiSearch size={13} className="filter-search-icon" />
              <input
                type="text"
                className="form-control"
                value={textDraft.erp_product}
                onChange={e => setTextDraft(d => ({ ...d, erp_product: e.target.value }))}
                onBlur={() => handleTextCommit('erp_product')}
                onKeyDown={e => e.key === 'Enter' && handleTextCommit('erp_product')}
              />
          </div>

          {hasActiveFilters && (
            <div className="filter-group" style={{ flex: '0 0 auto', marginLeft: 'auto' }}>
              <label style={{ visibility: 'hidden' }}>_</label>
              <button
                className="btn-secondary btn-sm"
                onClick={clearFilters}
                style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}
              >
                <FiX size={13} /> Limpar filtros
              </button>
            </div>
          )}

        </div>
      </div>

      <CalendarView
        events={events}
        loading={loading}
        title={t('areas.my_tasks_calendar', 'Calendário de Tarefas')}
      />

    </div>
  );
};

export default MyTasksCalendar;